import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_URL } from '../utils/apiConfig';
import api from '../api/client';
import { consumeOAuthPostAuthRedirect } from '../utils/oauthPostAuthRedirect';
import { clearSignupPostAuthRedirect } from '../utils/signupAuthRedirect';
import { markReturningUser } from '../utils/returningUser';
import { getApiFieldErrors, getApiValidationMessage } from '../utils/apiValidationError';

const AuthContext = createContext();

// Set axios base URL
axios.defaults.baseURL = API_URL;
// Debug: confirm API base URL in dev
if (process.env.NODE_ENV !== 'production') {
  // Using console.info to make it visible but not too noisy
  console.info('[AuthContext] API_URL:', API_URL);
  console.info('[AuthContext] axios.defaults.baseURL:', axios.defaults.baseURL);
}

// Add token to requests automatically
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ── Token refresh queue ──────────────────────────────────────────
// When a 401 arrives we try to refresh the token via /api/auth/verify.
// All other requests that receive a 401 while we're refreshing are queued
// and replayed once the refresh succeeds.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// Handle token expiration — attempt refresh before redirecting to login.
// Skip auth endpoints (login, register, verify) because a 401 there
// is an expected "wrong credentials" response, not a session expiry.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      const url = originalRequest?.url || '';
      const isAuthEndpoint =
        url.includes('/api/auth/login') ||
        url.includes('/api/auth/register') ||
        url.includes('/api/auth/verify') ||
        url.includes('/api/auth/forgot-password') ||
        url.includes('/api/auth/reset-password');

      // Don't attempt refresh for auth endpoints or if we've already tried
      if (isAuthEndpoint || originalRequest._retry) {
        return Promise.reject(error);
      }

      // If another request is already refreshing, queue this one
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        // No token at all — redirect unless on a public route
        isRefreshing = false;
        processQueue(new Error('No token'), null);
        localStorage.removeItem('user');
        const publicPaths = [
          '/landing',
          '/login',
          '/register',
          '/signup',
          '/forgot-password',
          '/reset-password',
          '/blog',
          '/interview-questions',
          '/guides',
          '/become-an-interviewer',
        ];
        const isPublicRoute =
          window.location.pathname === '/' ||
          publicPaths.some((p) => window.location.pathname.startsWith(p));
        if (!isPublicRoute) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        // Attempt to verify / refresh by hitting the verify endpoint
        const response = await axios.get('/api/auth/verify', {
          headers: { Authorization: `Bearer ${storedToken}` },
          _retry: true, // prevent infinite loop
        });

        if (response.data.success && response.data.token) {
          // Server issued a new token
          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }

          // Replay queued requests with new token
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axios(originalRequest);
        } else if (response.data.success) {
          // Token is still valid — replay with existing token
          processQueue(null, storedToken);
          return axios(originalRequest);
        } else {
          throw new Error('Token verification failed');
        }
      } catch (refreshError) {
        // Refresh failed — clear auth state and redirect (skip public routes)
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        const publicPaths = [
          '/landing',
          '/login',
          '/register',
          '/signup',
          '/forgot-password',
          '/reset-password',
          '/blog',
          '/interview-questions',
          '/guides',
          '/become-an-interviewer',
        ];
        const isPublicRoute =
          window.location.pathname === '/' ||
          publicPaths.some((p) => window.location.pathname.startsWith(p));
        if (!isPublicRoute) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 451 && error.response.data?.requiresConsent) {
      if (!window.location.pathname.startsWith('/consent')) {
        sessionStorage.setItem('consentRedirectTo', window.location.pathname);
        window.location.href = '/consent';
      }
    }

    return Promise.reject(error);
  }
);

// Small in-memory cache to dedupe rapid dashboard requests from multiple
// components (e.g. several components requesting dashboard on mount).
// This keeps a short TTL and also reuses an in-flight promise.
const DASHBOARD_CACHE_TTL = 5000; // ms
let dashboardCache = { ts: 0, data: null, promise: null };
const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === 'true' || process.env.NODE_ENV !== 'production';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [pendingConsents, setPendingConsents] = useState([]);
  const [consentChecked, setConsentChecked] = useState(false);

  const refreshPendingConsents = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setPendingConsents([]);
      setConsentChecked(true);
      return;
    }
    try {
      const { data } = await api.legal.getPending();
      setPendingConsents(data.pending || []);
    } catch {
      setPendingConsents([]);
    } finally {
      setConsentChecked(true);
    }
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Check if token is expired
          const decoded = jwtDecode(storedToken);
          const currentTime = Date.now() / 1000;

          if (decoded.exp > currentTime) {
            // Token is valid locally. Explicitly ensure the Authorization
            // header is present for the verify call (interceptor should
            // normally add it, but being explicit avoids race conditions).
            axios.defaults.headers.common.Authorization = `Bearer ${storedToken}`;

            try {
              const response = await axios.get('/api/auth/verify', {
                headers: { Authorization: `Bearer ${storedToken}` }
              });

              if (response.data.success) {
                // Prefer server-provided user when available
                setUser(response.data.user || JSON.parse(storedUser));
                setToken(storedToken);
                // Keep localStorage in sync with server (e.g. role changes to admin)
                if (response.data.user) {
                  localStorage.setItem('user', JSON.stringify(response.data.user));
                }
                refreshPendingConsents();
              } else {
                // Token invalid, clear storage
                localStorage.removeItem('token');
                localStorage.removeItem('user');
              }
            } catch (verifyError) {
              // If verify responds 401 it's expected for expired/invalid tokens —
              // clear auth state quietly to avoid noisy stack traces in dev.
              if (verifyError?.response?.status === 401) {
                console.info('[AuthContext] token verify returned 401, clearing auth state');
              } else {
                console.error('Token verification failed:', verifyError);
              }
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            }
          } else {
            // Token expired, clear storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password, captchaToken = null, deviceFingerprint = null) => {
    try {
      const requestData = {
        email,
        password
      };

      // Add CAPTCHA token if provided
      if (captchaToken) {
        requestData.captchaToken = captchaToken;
      }

      // Add device fingerprint if provided
      if (deviceFingerprint) {
        requestData.deviceFingerprint = deviceFingerprint;
      }

      const response = await axios.post(`${API_URL}/api/auth/login`, requestData);

      if (response.data.success) {
        const { user: userData, token: userToken } = response.data;
        
        // Store in localStorage
        localStorage.setItem('token', userToken);
        localStorage.setItem('user', JSON.stringify(userData));
        markReturningUser();
        
        // Update state
        setUser(userData);
        setToken(userToken);
        refreshPendingConsents();

        return { success: true, user: userData };
      } else {
        return { success: false, error: response.data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle rate limiting and CAPTCHA requirements
      if (error.response?.status === 429) {
        const errorData = error.response.data;
        return {
          success: false,
          error: errorData.error || 'Too many attempts',
          captchaRequired: errorData.captchaRequired || false,
          attempts: errorData.attempts || 0,
          deviceFingerprint: errorData.deviceFingerprint
        };
      }
      
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Login failed'
      };
    }
  };

  // Social login function for OAuth
  const socialLogin = async (userData, token) => {
    try {
      // Normalize shape: profile endpoint returns { success, user: {...} }
      const normalizedUser = userData && userData.user ? userData.user : userData;

      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      markReturningUser();
      
      // Update state
      setUser(normalizedUser);
      setToken(token);
      refreshPendingConsents();
      
      return { success: true, user: normalizedUser };
    } catch (error) {
      console.error('Social login error:', error);
      return {
        success: false,
        error: 'Social login failed'
      };
    }
  };

  // Register function
  const register = async (userData, captchaToken = null, deviceFingerprint = null) => {
    try {
      const requestData = { ...userData };
      
      // Add CAPTCHA token if provided
      if (captchaToken) {
        requestData.captchaToken = captchaToken;
      }

      // Add device fingerprint if provided
      if (deviceFingerprint) {
        requestData.deviceFingerprint = deviceFingerprint;
      }
      
      console.log('Attempting registration with:', { ...requestData, password: '[HIDDEN]', confirmPassword: '[HIDDEN]' });
      const response = await axios.post('/api/auth/register', requestData);
      console.log('Registration response:', response.data);

      if (response.data.success) {
        const { user: newUser, token: userToken, emailVerificationEnabled } = response.data;
        
        // Store token for API requests
        localStorage.setItem('token', userToken);
        markReturningUser();
        setToken(userToken);
        
        // Only set user state and localStorage if email is verified
        // This prevents automatic login for unverified users
        if (newUser.email_verified) {
          localStorage.setItem('user', JSON.stringify(newUser));
          setUser(newUser);
        } else {
          // For unverified users, don't set user state
          // This keeps them in the AuthWrapper for email verification
          localStorage.removeItem('user');
        }
        
        return {
          success: true,
          user: newUser,
          emailVerificationEnabled,
          devEmailHint: response.data.devEmailHint || null,
        };
      } else {
        return {
          success: false,
          error: response.data.message || 'Registration failed'
        };
      }
    } catch (error) {
      console.error('Registration error details:', {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config,
        code: error.code,
        stack: error.stack
      });
      
      // More specific error handling
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        console.error('Network connectivity issue detected');
        return {
          success: false,
          error: 'Unable to connect to server. Please check your internet connection and try again.'
        };
      }
      
      if (error.response) {
        // Handle rate limiting and CAPTCHA requirements
        if (error.response.status === 429) {
          const errorData = error.response.data || {};
          const url = error.config?.url || '';
          const isRegisterEndpoint = url.includes('/api/auth/register');
          const hasCaptchaHint = (errorData.captchaRequired === true) ||
            (/captcha/i.test(errorData.error || '') || /captcha/i.test(errorData.message || ''));
          const shouldRequireCaptcha = errorData.captchaRequired || isRegisterEndpoint || hasCaptchaHint;
          return {
            success: false,
            error: errorData.error || 'Too many attempts',
            captchaRequired: !!shouldRequireCaptcha,
            attempts: errorData.attempts || 0,
            deviceFingerprint: errorData.deviceFingerprint
          };
        }
        
        // Server responded with error status
        return {
          success: false,
          error: getApiValidationMessage(
            error.response.data,
            error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`
          ),
          fieldErrors: getApiFieldErrors(error.response.data),
        };
      } else if (error.request) {
        // Request was made but no response received
        return {
          success: false,
          error: 'No response from server. Please check if the server is running.'
        };
      } else {
        // Something else happened
        return {
          success: false,
          error: error.message || 'Registration failed'
        };
      }
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (token) {
        await axios.post('/api/auth/logout');
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear storage and state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setToken(null);
      setPendingConsents([]);
      setConsentChecked(false);
      consumeOAuthPostAuthRedirect();
      clearSignupPostAuthRedirect();
    }
  };

  // Refresh user data from backend
  const refreshUser = async () => {
    try {
      if (!token) {
        console.log('No token available for refresh');
        return { success: false, error: 'No token available' };
      }

      console.log('Refreshing user data from backend...');
      const response = await axios.get('/api/auth/verify');
      
      if (response.data.success) {
        const updatedUser = response.data.user;
        console.log('User data refreshed:', updatedUser);
        
        // Update state and localStorage
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        return { success: true, user: updatedUser };
      } else {
        console.error('Failed to refresh user data:', response.data);
        return { success: false, error: 'Failed to refresh user data' };
      }
    } catch (error) {
      console.error('Refresh user error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to refresh user data'
      };
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);
      
      if (response.data.success) {
        // Fetch updated user data with cache-busting
        const cacheBuster = Date.now();
        const profileResponse = await axios.get(`/api/auth/profile?_t=${cacheBuster}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        if (profileResponse.data.success) {
          const updatedUser = profileResponse.data.user;
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        return { success: true };
      }
    } catch (error) {
      console.error('Profile update error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Profile update failed'
      };
    }
  };

  // Verify email OTP
  const verifyEmailOTP = async (email, otp) => {
    try {
      const response = await axios.post('/api/auth/verify-email-otp', {
        email,
        otp
      });

      if (response.data.success) {
        // Get the verified user from response or update existing user
        const verifiedUser = response.data.user || { ...user, email_verified: true };
        setUser(verifiedUser);
        localStorage.setItem('user', JSON.stringify(verifiedUser));
        
        return { success: true, user: verifiedUser };
      } else {
        return {
          success: false,
          error: response.data.message || 'OTP verification failed'
        };
      }
    } catch (error) {
      console.error('Email OTP verification error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'OTP verification failed'
      };
    }
  };

  // Resend email OTP
  const resendEmailOTP = async (email) => {
    try {
      const response = await axios.post('/api/auth/resend-email-otp', {
        email
      });

      if (response.data.success) {
        return {
          success: true,
          devEmailHint: response.data.devEmailHint || null,
          message: response.data.message || null,
        };
      } else {
        return {
          success: false,
          error: response.data.message || 'Failed to resend OTP'
        };
      }
    } catch (error) {
      console.error('Resend email OTP error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || 'Failed to resend OTP'
      };
    }
  };

  // Get dashboard data
  const getDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (DEBUG_AUTH) {
        console.log('🔍 Dashboard API Debug - Token exists:', !!token);
        console.log('🔍 Dashboard API Debug - Token length:', token?.length);
        console.log('🔍 Dashboard API Debug - Token value (first 50 chars):', token?.substring(0, 50));
      }
      
      // Try to decode the token to see if it's valid
      if (token) {
        try {
          const tokenParts = token.split('.');
          console.log('🔍 Dashboard API Debug - Token parts count:', tokenParts.length);
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            console.log('🔍 Dashboard API Debug - Token payload:', payload);
            console.log('🔍 Dashboard API Debug - Token expires:', new Date(payload.exp * 1000));
            console.log('🔍 Dashboard API Debug - Token expired?', Date.now() > payload.exp * 1000);
          }
        } catch (decodeError) {
          console.error('🔍 Dashboard API Debug - Token decode error:', decodeError);
        }
      }
      
      console.log('🔍 Dashboard API Debug - API URL:', API_URL);
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Dedupe recent requests using a tiny in-memory cache
      const now = Date.now();
      if (dashboardCache.data && (now - dashboardCache.ts) < DASHBOARD_CACHE_TTL) {
        if (DEBUG_AUTH) console.log('[AuthContext] Returning dashboard data from cache');
        return dashboardCache.data;
      }

      if (dashboardCache.promise) {
        if (DEBUG_AUTH) console.log('[AuthContext] Awaiting in-flight dashboard promise');
        return dashboardCache.promise;
      }

      const cacheBuster = now;
      const requestPromise = (async () => {
        const response = await axios.get(`/api/auth/dashboard?_t=${cacheBuster}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });

        // store for short TTL
        dashboardCache = { ts: Date.now(), data: response.data, promise: null };
        if (DEBUG_AUTH) console.log('getDashboardData - Response received:', response.status);
        return response.data;
      })();

      dashboardCache.promise = requestPromise;
      try {
        return await requestPromise;
      } finally {
        dashboardCache.promise = null;
      }
    } catch (error) {
      if (DEBUG_AUTH) {
        console.error('🚨 Dashboard fetch error - Full error object:', error);
        console.error('🚨 Dashboard fetch error details:', {
          message: error.message,
          name: error.name,
          code: error.code,
          response: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL,
            headers: error.config?.headers
          },
          token: !!localStorage.getItem('token'),
          tokenLength: localStorage.getItem('token')?.length
        });

        if (error.response) {
          console.error('🚨 Server responded with error:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('🚨 No response received - network error:', error.request);
        } else {
          console.error('🚨 Request setup error:', error.message);
        }
      }

      throw error;
    }
  };

  // Check if user has subscription level
  const hasSubscription = (requiredLevel = 'free') => {
    if (!user) return false;
    
    const levels = {
      'free': 0,
      'premium': 1,
      'professional': 2,
      'enterprise': 3
    };
    
    const sub = typeof user.subscription === 'string' ? user.subscription.toLowerCase() : 'free';
    const userLevel = levels[sub] || 0;
    const requiredLevelNum = levels[requiredLevel] || 0;
    
    return userLevel >= requiredLevelNum;
  };

  // Get subscription limits
  const getSubscriptionLimits = () => {
    const limits = {
      'free': {
        interviewsPerMonth: 3,
        questionsPerInterview: 5,
        aiRecommendations: false,
        progressTracking: false
      },
      'premium': {
        interviewsPerMonth: 50,
        questionsPerInterview: 15,
        aiRecommendations: true,
        progressTracking: true
      },
      'professional': {
        interviewsPerMonth: 200,
        questionsPerInterview: 25,
        aiRecommendations: true,
        progressTracking: true
      },
      'enterprise': {
        interviewsPerMonth: -1, // unlimited
        questionsPerInterview: -1, // unlimited
        aiRecommendations: true,
        progressTracking: true
      }
    };
    
    return limits[user?.subscription] || limits.free;
  };

  // Lightweight in-place user update — merges patch into current user object
  // and persists to localStorage. Use after saving preferences without a full
  // round-trip to /api/auth/profile.
  const updateUser = (patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    user,
    token,
    loading,
    login,
    socialLogin,
    register,
    logout,
    refreshUser,
    updateProfile,
    updateUser,
    verifyEmailOTP,
    resendEmailOTP,
    getDashboardData,
    hasSubscription,
    getSubscriptionLimits,
    pendingConsents,
    consentChecked,
    refreshPendingConsents,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;